import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import useCampaign from "hooks/useCampaign";
import { DateTime } from "luxon";

export default function data() {
  const navigate = useNavigate();
  const { getCampaignList, deleteCampaign, campaignList } = useCampaign();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState({});

  const handleDeleteCampaign = async (campaignId) => {
    if (window.confirm("Are you sure you want to delete this campaign?")) {
      setLoading((prev) => ({ ...prev, [campaignId]: { ...prev[campaignId], delete: true } }));
      try {
        const res = await deleteCampaign(campaignId);
        if (res.success) {
          alert("Campaign deleted successfully!");
          fetchList();
        }
      } catch (err) {
        console.error("Error deleting campaign:", err);
        alert("An error occurred while deleting the campaign.");
      } finally {
        setLoading((prev) => ({ ...prev, [campaignId]: { ...prev[campaignId], delete: false } }));
      }
    }
  };

  const getStatus = (startDate, endDate) => {
    const nowUTC = DateTime.utc();
    const startUTC = DateTime.fromISO(startDate, { zone: "utc" });
    const endUTC = DateTime.fromISO(endDate, { zone: "utc" });

    if (nowUTC < startUTC) {
      return "Upcoming";
    } else if (nowUTC >= startUTC && nowUTC <= endUTC) {
      return "Active";
    } else {
      return "Expired";
    }
  };

  const CampaignActions = ({ onDelete, onEdit }) => (
    <MDBox display="flex" justifyContent="center">
      <MDButton variant="contained" color="info" onClick={onEdit} sx={{ mr: 1 }}>
        Edit
      </MDButton>
      <MDButton variant="contained" color="error" onClick={onDelete}>
        Delete
      </MDButton>
    </MDBox>
  );

  const CampaignInfo = ({ name, startDate, endDate }) => (
    <MDBox display="flex" alignItems="center" lineHeight={1}>
      <MDBox ml={2} lineHeight={1}>
        <MDTypography display="block" variant="button" fontWeight="medium">
          {name}
        </MDTypography>
        <MDTypography variant="caption" color="text" display="block">
          Start: {startDate?.split("T")[0]} | End: {endDate?.split("T")[0]}
        </MDTypography>
      </MDBox>
    </MDBox>
  );

  const FeaturedStatus = ({ featured }) => (
    <MDBox display="flex" justifyContent="center">
      <MDTypography
        variant="caption"
        color={featured ? "success" : "text"}
        fontWeight={featured ? "bold" : "regular"}
      >
        {featured ? "Yes" : "No"}
      </MDTypography>
    </MDBox>
  );

  const rows = data?.map((campaign) => ({
    campaign: (
      <CampaignInfo
        name={campaign.name}
        startDate={campaign.startDate}
        endDate={campaign.endDate}
      />
    ),
    startDate: campaign?.startDate?.split("T")[0],
    endDate: campaign?.endDate?.split("T")[0],
    status: getStatus(campaign.startDate, campaign.endDate),
    featured: <FeaturedStatus featured={campaign.featured || false} />,
    actions: (
      <CampaignActions
        onDelete={() => handleDeleteCampaign(campaign._id)}
        onEdit={() => navigate(`/campaign-management/edit?id=${campaign._id}`)}
      />
    ),
  }));

  const fetchList = async () => {
    await getCampaignList();
  };

  useEffect(() => {
    if (campaignList.campaigns) {
      setData(campaignList.campaigns);
    }
  }, [campaignList]);

  return {
    columns: [
      { Header: "Campaign", accessor: "campaign", width: "30%", align: "left" },
      { Header: "Start Date", accessor: "startDate", align: "center" },
      { Header: "End Date", accessor: "endDate", align: "center" },
      { Header: "Status", accessor: "status", align: "center" },
      { Header: "Featured", accessor: "featured", align: "center" },
      { Header: "Actions", accessor: "actions", align: "center" },
    ],
    rows,
  };
}
